import { BuySeatsDialog } from "@/components/common/BuySeatsDialog";
import { EmptyState } from "@/components/common/EmptyState";
import { PaymentMethodCard } from "@/components/common/PaymentMethodCard";
import { ScheduledSeatsCard } from "@/components/common/ScheduledSeatsCard";
import { InvoiceStatusBadge } from "@/components/common/StatusBadge";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { useInvoices, useOrg, useRefreshInvoice } from "@/hooks";
import { describeInvoice } from "@/lib/invoice";
import { cycleNoun, formatMoney } from "@/lib/money";
import { useState } from "react";

export function BillingPage() {
	const { data, isLoading } = useOrg();
	const invoices = useInvoices();
	const refresh = useRefreshInvoice();
	const [buying, setBuying] = useState(false);

	if (isLoading) return <Skeleton className="h-48 w-full" />;

	const plan = data?.plan;
	const seats = data?.seats;
	const currency = plan?.currency ?? "USD";
	const per = cycleNoun(plan?.billing_cycle);

	// A flat-fee contract has NO per-seat rate — seat_price_cents is null by
	// design. Reading it regardless is why this screen showed "—" for a
	// perfectly valid ₹60,000/month contract.
	const isFlat = plan?.pricing_model === "flat_fee";

	// null seats means unlimited, and Number(null) would render it as 0 —
	// "0 seats purchased" is the opposite of what an unlimited contract means.
	const unlimited = seats?.purchased == null;

	return (
		<>
			<PageHeader
				title="Billing"
				description="Your plan, seats and invoices."
				action={
					// Nothing to buy on an unlimited plan, and a flat-fee contract
					// with a cap is a renegotiation rather than a self-serve
					// purchase — both route to a conversation instead.
					!unlimited && !isFlat ? (
						<Button variant="secondary" onClick={() => setBuying(true)}>
							Add seats
						</Button>
					) : undefined
				}
			/>

			<Card>
				<CardContent className="grid gap-5 p-5 sm:grid-cols-2">
					<Field label="Plan" value={plan?.name ?? "—"} />

					{isFlat ? (
						<Field
							label={`Cost per ${per}`}
							value={
								plan?.flat_amount_cents != null
									? formatMoney(plan.flat_amount_cents, currency)
									: "—"
							}
						/>
					) : (
						<Field
							label="Per seat"
							value={
								plan?.seat_price_cents != null
									? `${formatMoney(plan.seat_price_cents, currency)} / ${per}`
									: "—"
							}
						/>
					)}

					<Field
						label="Seats purchased"
						value={unlimited ? "Unlimited" : String(seats?.purchased ?? "—")}
					/>
					<Field label="Seats in use" value={String(seats?.active ?? "—")} />

					{!isFlat && !unlimited ? (
						<Field
							label={`Total per ${per}`}
							value={
								plan?.seat_price_cents != null && seats?.purchased != null
									? formatMoney(
											plan.seat_price_cents * seats.purchased,
											currency,
										)
									: "—"
							}
						/>
					) : null}

					<Field
						label="Contract"
						value={
							data?.org.contract_ends_at
								? `Until ${new Date(data.org.contract_ends_at).toLocaleDateString()}`
								: "Open-ended"
						}
					/>
					{/* Reducing the pool is the half that was missing: an org
					    could buy seats and never give any back. */}
					{!isFlat && !unlimited ? (
						<div className="sm:col-span-2">
							<ScheduledSeatsCard />
						</div>
					) : null}
				</CardContent>
			</Card>

			<div className="mt-6">
				<PaymentMethodCard />
			</div>

			<h2 className="mb-3 mt-8 text-title3 font-medium text-heading">
				Invoices
			</h2>

			{invoices.isLoading ? (
				<Skeleton className="h-40 w-full" />
			) : (invoices.data?.invoices.length ?? 0) === 0 ? (
				<EmptyState
					title="No invoices yet"
					description={`Your first invoice appears here once your ${per} is billed.`}
				/>
			) : (
				<Card>
					<div className="overflow-x-auto">
						<table className="w-full text-body">
							<thead>
								<tr className="border-b border-divider text-caption uppercase tracking-wide text-muted-foreground">
									<Th>Issued</Th>
									<Th>For</Th>
									<Th>Period</Th>
									<Th className="text-right">Amount</Th>
									<Th>Status</Th>
									<Th />
								</tr>
							</thead>
							<tbody>
								{invoices.data?.invoices.map((inv) => (
									<tr
										key={`${inv.kind}-${inv.period_start}-${inv.issued_at}`}
										className="border-b border-divider last:border-0"
									>
										<Td className="tabular text-muted-foreground">
											{fmt(inv.issued_at)}
										</Td>
										<Td>
											{describeInvoice(inv)}
											{/*
											 * An invoice that was MEANT to be auto-charged and
											 * wasn't is the one row an admin has to act on. Left
											 * unexplained it looks identical to a normal invoice,
											 * and the org drifts toward dunning without anyone
											 * understanding why the money never moved.
											 */}
											{inv.collection_mode === "auto_charge" &&
											inv.status !== "paid" &&
											inv.auto_charge_last_error ? (
												<div className="mt-0.5 text-caption text-orange-text">
													Not charged automatically —{" "}
													{inv.auto_charge_last_error}
												</div>
											) : null}
										</Td>
										<Td className="tabular text-muted-foreground">
											{fmt(inv.period_start)} – {fmt(inv.period_end)}
										</Td>
										<Td className="tabular text-right font-medium text-heading">
											{formatMoney(inv.total_cents, inv.currency)}
										</Td>
										<Td>
											<InvoiceStatusBadge
												status={inv.status}
												dueAt={inv.due_at}
											/>
										</Td>
										<Td className="text-right">
											{/*
											 * A payment confirmation can be missed in
											 * transit, which leaves an invoice reading
											 * Due after the money has left. Rather than
											 * make an admin wait on us noticing, let
											 * them ask the provider directly.
											 */}
											{inv.status !== "paid" ? (
												<Button
													variant="ghost"
													size="sm"
													disabled={refresh.isPending}
													onClick={() => refresh.mutate(inv.id)}
													title="Already paid this? Check with the payment provider."
												>
													{refresh.isPending ? "Checking…" : "Check"}
												</Button>
											) : null}
											{inv.short_url && inv.status !== "paid" ? (
												<Button asChild variant="ghost" size="sm">
													<a
														href={inv.short_url}
														target="_blank"
														rel="noreferrer"
													>
														Pay
													</a>
												</Button>
											) : inv.short_url ? (
												<Button asChild variant="ghost" size="sm">
													<a
														href={inv.short_url}
														target="_blank"
														rel="noreferrer"
													>
														View
													</a>
												</Button>
											) : null}
										</Td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				</Card>
			)}

			<p className="mt-4 text-caption text-muted-foreground">
				{isFlat || unlimited
					? "Your plan covers your whole team. To change it, contact your Curo account manager."
					: `Seats you add are charged for the rest of the current ${per}. Removing someone frees their seat for the rest of the ${per} at no extra cost.`}
			</p>

			<BuySeatsDialog
				open={buying}
				onOpenChange={setBuying}
				cycle={plan?.billing_cycle}
				currentSeats={seats?.purchased ?? null}
			/>
		</>
	);
}

function fmt(iso: string | null): string {
	if (!iso) return "—";
	return new Date(iso).toLocaleDateString(undefined, {
		day: "numeric",
		month: "short",
		year: "numeric",
	});
}

function Field({ label, value }: { label: string; value: string }) {
	return (
		<div>
			<div className="text-caption text-muted-foreground">{label}</div>
			<div className="tabular mt-0.5 text-title2 font-medium text-heading">
				{value}
			</div>
		</div>
	);
}

function Th({
	children,
	className,
}: { children?: React.ReactNode; className?: string }) {
	return (
		<th className={`px-4 py-2.5 text-left font-medium ${className ?? ""}`}>
			{children}
		</th>
	);
}

function Td({
	children,
	className,
}: { children?: React.ReactNode; className?: string }) {
	return <td className={`px-4 py-3 ${className ?? ""}`}>{children}</td>;
}
